// src/components/common/MedicineSelect.jsx
import React, { useContext, useEffect, useState } from "react";
import { TextField, MenuItem, ListSubheader } from "@mui/material";
import { CommonContext } from "../../context/CommonContext";
import { apisPath } from "../../utils/path";
import { groupMedicinesByCancerType } from "../../helpers/commonHelper";


const MedicineSelect = ({
    name,
    value,
    onChange,
    onBlur,
    label = "Drug Name",
    required = false,
    error = false,
    helperText = "",
    disabled = false,
    fullWidth = true,
    size = "medium",
    variant = "outlined",
    InputLabelProps = {},
    SelectProps = {},
    ...otherProps
}) => {
    const { getMasterData, setIsLoading, setAlertState } = useContext(CommonContext);
    const [groupedMedicines, setGroupedMedicines] = useState({});

    // Fetch medicines on mount
    useEffect(() => {
        const loadMedicines = async () => {
            setIsLoading(true);
            try {
                const res = await getMasterData(apisPath?.masters?.medicineMaster, "medicines");
                if (res) {
                    const grouped = groupMedicinesByCancerType(res);
                    setGroupedMedicines(grouped);
                }
            } catch (error) {
                console.error("Error loading medicines:", error);
                setAlertState({
                    open: true,
                    title: "Failed to load medicines. Please refresh the page.",
                    btnTxt: "CLOSE",
                    status: "fail",
                });
            } finally {
                setIsLoading(false);
            }
        };
        loadMedicines();
    }, []);

    // Render grouped medicine options
    const renderMedicineOptions = () => {
        const items = [];

        Object.keys(groupedMedicines).sort().forEach((cancerType) => {
            // Add group header
            items.push(
                <ListSubheader 
                    key={`header-${cancerType}`} 
                    sx={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}
                >
                    {cancerType}
                </ListSubheader>
            );

            // Add medicines in this group
            groupedMedicines[cancerType].forEach((medicine) => {
                items.push(
                    <MenuItem key={medicine?.medicine_id} value={medicine?.name}>
                        {medicine?.name}
                    </MenuItem>
                );
            });
        });

        return items;
    };

    return (
        <TextField
            select
            name={name}
            label={label}
            value={value || ""}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            error={error}
            helperText={helperText}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            InputLabelProps={{
                shrink: true,
                ...InputLabelProps
            }}
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        style: {
                            maxHeight: 400,
                        },
                    },
                },
                displayEmpty: true,
                ...SelectProps
            }}
            {...otherProps}
        >
            <MenuItem value="">
                <em>Select</em>
            </MenuItem>
            {renderMedicineOptions()}
        </TextField>
    );
};

export default MedicineSelect;